import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { authToken } from '../globals/Global';
import { getAartiData, createAartiByAdmin, updateAartiByAdmin, deleteAartiByAdmin, createAartiContentByAdmin, updateAartiContentByAdmin, deleteAartiContentByAdmin, importAartiCsv } from '../globals/Services';

export default function AartiManagement() {
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [newCategory, setNewCategory] = useState({ titleName: '', name: '', imageUrl: '' });
  const [newSubCategory, setNewSubCategory] = useState({
    aartiCategoryId: '',
    name: '',
    description: '',
    imageUrl: '',
  });
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);

  // --- Dialog Management State ---
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [openSubCategoryDialog, setOpenSubCategoryDialog] = useState(false);
  const [openEditCategoryDialog, setOpenEditCategoryDialog] = useState(false);
  const [openEditSubCategoryDialog, setOpenEditSubCategoryDialog] = useState(false);
  const [openDeleteCategoryDialog, setOpenDeleteCategoryDialog] = useState(false);
  const [openDeleteSubCategoryDialog, setOpenDeleteSubCategoryDialog] = useState(false);

  // --- Upload CSV State ---
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [uploadFiles, setUploadFiles] = useState({ details: null, content: null });
  const [uploading, setUploading] = useState(false);

  // --- YouTube CSV Upload State ---
  const [openYouTubeDialog, setOpenYouTubeDialog] = useState(false);
  const [youtubeFile, setYoutubeFile] = useState(null);
  const [uploadingYoutube, setUploadingYoutube] = useState(false);

  // --- State for Items Being Edited or Deleted ---
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSubCategory, setEditingSubCategory] = useState(null);
  const [deletingCategory, setDeletingCategory] = useState(null);
  const [deletingSubCategory, setDeletingSubCategory] = useState(null);

  // --- State to trigger data refetching ---
  const [refetch, setRefetch] = useState(0);
  const triggerRefetch = () => setRefetch(prev => prev + 1);

  // --- Data Fetching ---
  const fetchAartiData = useCallback(async () => {
    try {
      const token = authToken || localStorage.getItem('sessionToken');
      const res = await fetch(getAartiData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error('Failed to fetch data');

      const result = await res.json();
      const aartiData = result?.data || [];

      const transformedCategories = aartiData.map(cat => ({
        id: cat.aartiId,
        titleName: cat.titleName,
        name: cat.aartiGodName,
        imageUrl: cat.aartiGodImageUrl || '',
      }));
      setCategories(transformedCategories);

      const allSubCategories = aartiData.flatMap(cat =>
      (cat.aartiContentDetails || []).map(sub => ({
        id: sub.aartiContentId,
        aartiCategoryId: cat.aartiId,
        name: sub.aartiName,
        description: sub.aartiContent,
        imageUrl: sub.aartiImageUrl,
        videoUrls: sub.aartiVideoUrls || '',
        status: sub.status || 'active',
      }))
    );
      setSubCategories(allSubCategories);

      if (!selectedCategoryId && transformedCategories.length > 0) {
        setSelectedCategoryId(transformedCategories[0].id);
      }

      if (transformedCategories.length > 0) {
        setNewSubCategory(prev => ({ ...prev, aartiCategoryId: prev.aartiCategoryId || transformedCategories[0].id }));
      }
    } catch (error) {
      console.error('Failed to fetch Aarti data:', error);
      toast.error('Error fetching Aarti data');
    }
  }, [selectedCategoryId]);

  useEffect(() => {
    fetchAartiData();
  }, [refetch, fetchAartiData]);

  const getAuthToken = () => authToken || localStorage.getItem('sessionToken');

  // --- YouTube CSV Upload Handler ---
  const handleYouTubeCsvUpload = async () => {
    if (!youtubeFile) {
      toast.error('Please select a CSV file.');
      return;
    }
    setUploadingYoutube(true);
    try {
      const formData = new FormData();
      formData.append('file', youtubeFile);

      const token = authToken || localStorage.getItem('sessionToken');

      if (!token) {
        toast.error('Session expired. Please login again.');
        setUploadingYoutube(false);
        return;
      }

      const res = await fetch('http://stg.panditjee.com:8080/api/aartiDetails/aarti-content-details/update-import', {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const contentType = res.headers.get('content-type');
      const result = contentType?.includes('application/json') ? await res.json() : await res.text();

      if (!res.ok) throw new Error(result?.message || result || 'YouTube CSV upload failed.');

      toast.success(`YouTube CSV uploaded successfully! Processed: ${result.processed}, Inserted: ${result.inserted}`);
      setOpenYouTubeDialog(false);
      setYoutubeFile(null);
      triggerRefetch();
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setUploadingYoutube(false);
    }
  };

  // --- Upload CSV Handler ---
  const handleUploadCsv = async () => {
  if (!uploadFiles.details || !uploadFiles.content) {
    toast.error('Please select both files.');
    return;
  }
  setUploading(true);
  try {
    const formData = new FormData();
    formData.append('details', uploadFiles.details);
    formData.append('content', uploadFiles.content);

    // ✅ Get token explicitly before fetch
    const token = authToken || localStorage.getItem('sessionToken');

    console.log('Token being sent:', token); // 👈 Add this to verify token exists

    if (!token) {
      toast.error('Session expired. Please login again.');
      setUploading(false);
      return;
    }

    const res = await fetch(importAartiCsv, {
      method: 'POST',
      headers: { 
        Authorization: `Bearer ${token}`,
        // ✅ Do NOT set Content-Type here — browser sets it automatically with boundary for FormData
      },
      body: formData,
    });

    // ✅ Handle non-JSON error responses too
    const contentType = res.headers.get('content-type');
    const result = contentType?.includes('application/json') ? await res.json() : await res.text();

    if (!res.ok) throw new Error(result?.message || result || 'Upload failed.');

    toast.success('CSV uploaded successfully!');
    setOpenUploadDialog(false);
    setUploadFiles({ details: null, content: null });
    triggerRefetch();
  } catch (error) {
    toast.error(`Error: ${error.message}`);
  } finally {
    setUploading(false);
  }
};

  // --- Category CRUD Handlers ---
  const handleCreateCategory = async () => {
    try {
      const res = await fetch(createAartiByAdmin, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
        body: JSON.stringify({ titleName: newCategory.titleName, aartiGodName: newCategory.name, aartiGodImageUrl: newCategory.imageUrl }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.message || 'Could not create category.');

      toast.success('Category created successfully!');
      setOpenCategoryDialog(false);
      setNewCategory({ titleName: '', name: '', imageUrl: '' });
      triggerRefetch();
    } catch (error) {
      console.error('API error:', error);
      toast.error(`Error: ${error.message}`);
    }
  };

const handleUpdateCategory = async () => {
  if (!editingCategory) return;

  try {
    const token = getAuthToken();

    if (!token) {
      toast.error('Session expired. Please login again.');
      return;
    }

    console.log('=== UPDATE CATEGORY ===');
    console.log('Category ID:', editingCategory.id);
    console.log('Has new image file:', !!editingCategory.imageFile);

    const formData = new FormData();

    // Common fields
    formData.append('aartiId', Number(editingCategory.id));
    formData.append('titleName', editingCategory.titleName || '');
    formData.append('aartiGodName', editingCategory.name || '');

    // Existing image URL (important if no new image)
    if (editingCategory.imageUrl) {
      formData.append('aartiGodImageUrl', editingCategory.imageUrl);
    }

    // If new image exists → append it
    if (editingCategory.imageFile) {
      formData.append('file', editingCategory.imageFile);
    }

    // Debug FormData
    console.log('FormData contents:');
    for (let [key, value] of formData.entries()) {
      if (value instanceof File) {
        console.log(`  ${key}: [File] ${value.name} (${value.size} bytes)`);
      } else {
        console.log(`  ${key}: ${value}`);
      }
    }

    const res = await fetch(updateAartiByAdmin, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        // ❌ Do NOT set Content-Type
      },
      body: formData,
    });

    console.log('Response Status:', res.status);

    const contentType = res.headers.get('content-type');
    let result;

    if (contentType?.includes('application/json')) {
      result = await res.json();
    } else {
      const text = await res.text();
      console.log('Non-JSON Response:', text);
      result = { message: text };
    }

    console.log('Response Data:', result);

    if (!res.ok) {
      throw new Error(
        result?.message ||
        result?.errorMessage ||
        `Update failed with status ${res.status}`
      );
    }

    toast.success('Category updated successfully!');
    setOpenEditCategoryDialog(false);
    setEditingCategory(null);
    triggerRefetch();

  } catch (error) {
    console.error('=== UPDATE ERROR ===');
    console.error('Error message:', error.message);
    console.error('Full error:', error);
    toast.error(`Error: ${error.message}`);
  }
};

  const handleDeleteCategory = async () => {
    if (!deletingCategory) return;
    try {
      const res = await fetch(deleteAartiByAdmin(deletingCategory.id), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });

      if (!res.ok) {
        const result = await res.json();
        throw new Error(result.message || 'Could not delete category.');
      }

      toast.success('Category deleted successfully!');
      setOpenDeleteCategoryDialog(false);
      setSelectedCategoryId(null);
      triggerRefetch();
    } catch (error) {
      console.error('API error:', error);
      toast.error(`Error: ${error.message}`);
    }
  };

  // --- Sub-Category CRUD Handlers ---
  const handleCreateSubCategory = async () => {
    try {
      const res = await fetch(createAartiContentByAdmin, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
        body: JSON.stringify({
          aartiId: Number(newSubCategory.aartiCategoryId),
          aartiName: newSubCategory.name,
          aartiContent: newSubCategory.description,
          aartiImageUrl: newSubCategory.imageUrl,
        }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.message || 'Could not create sub-category.');

      toast.success('Sub-category created successfully!');
      setOpenSubCategoryDialog(false);
      setNewSubCategory({ aartiCategoryId: newSubCategory.aartiCategoryId, name: '', description: '', imageUrl: '' });
      triggerRefetch();
    } catch (error) {
      console.error('API error:', error);
      toast.error(`Error: ${error.message}`);
    }
  };

    const handleUpdateSubCategory = async () => {
      if (!editingSubCategory) return;
      try {
        const formData = new FormData();
        formData.append('aartiContentId', Number(editingSubCategory.id));
        formData.append('aartiName', editingSubCategory.name);
        formData.append('aartiContent', editingSubCategory.description);
        formData.append('aartiVideoUrls', editingSubCategory.videoUrls || '');
        formData.append('status', editingSubCategory.status || 'active');

        if (editingSubCategory.imageFile) {
          formData.append('file', editingSubCategory.imageFile);
        }

        const res = await fetch(updateAartiContentByAdmin, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${getAuthToken()}` }, // ✅ No Content-Type for FormData
          body: formData,
        });
        const result = await res.json();
        if (!res.ok) throw new Error(result.message || 'Could not update sub-category.');

        toast.success('Sub-category updated successfully!');
        setOpenEditSubCategoryDialog(false);
        triggerRefetch();
      } catch (error) {
        console.error('API error:', error);
        toast.error(`Error: ${error.message}`);
      }
    };

  const handleDeleteSubCategory = async () => {
    if (!deletingSubCategory) return;
    try {
      const res = await fetch(deleteAartiContentByAdmin(deletingSubCategory.id), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });

      if (!res.ok) {
        const result = await res.json();
        throw new Error(result.message || 'Could not delete sub-category.');
      }

      toast.success('Sub-category deleted successfully!');
      setOpenDeleteSubCategoryDialog(false);
      triggerRefetch();
    } catch (error) {
      console.error('API error:', error);
      toast.error(`Error: ${error.message}`);
    }
  };

  // --- Dialog Open/Close Handlers ---
  const openEditCategory = (cat) => {
    setEditingCategory(cat);
    setOpenEditCategoryDialog(true);
  };

  const openDeleteCategory = (cat) => {
    setDeletingCategory(cat);
    setOpenDeleteCategoryDialog(true);
  };

  const openEditSubCategory = (sub) => {
    setEditingSubCategory(sub);
    setOpenEditSubCategoryDialog(true);
  };

  const openDeleteSubCategory = (sub) => {
    setDeletingSubCategory(sub);
    setOpenDeleteSubCategoryDialog(true);
  };

  const filteredSubCategories = selectedCategoryId
    ? subCategories.filter(sub => String(sub.aartiCategoryId) === String(selectedCategoryId))
    : [];

  const getSubCategoryCount = (categoryId) => {
    return subCategories.filter(sub => String(sub.aartiCategoryId) === String(categoryId)).length;
  };

  return (
    <Box sx={{ height: '100vh', overflow: 'hidden', p: 2 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight="600">
          Aarti Management
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setOpenYouTubeDialog(true)}
            sx={{ minWidth: '180px', fontWeight: 'bold', textTransform: 'none' }}
          >
            📺 YouTube CSV
          </Button>
          <Button
            variant="outlined"
            color="success"
            onClick={() => setOpenUploadDialog(true)}
            sx={{ minWidth: '160px', fontWeight: 'bold', textTransform: 'none' }}
          >
            ⬆ Upload CSV
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpenCategoryDialog(true)}
            sx={{ minWidth: '180px', fontWeight: 'bold', textTransform: 'none' }}
          >
            + Add Aarti Category
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => setOpenSubCategoryDialog(true)}
            disabled={categories.length === 0}
            sx={{ minWidth: '180px', fontWeight: 'bold', textTransform: 'none' }}
          >
            + Add Aarti Content
          </Button>
        </Box>
      </Box>

      {/* Main Content Grid */}
      <Box sx={{ display: 'flex', gap: 2, height: 'calc(100vh - 100px)' }}>

        {/* Left Column - Categories */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" gutterBottom fontWeight="bold">Aarti Categories</Typography>
              <Box sx={{ flex: 1, overflow: 'auto' }}>
                {categories.length > 0 ? (
                  categories.map(cat => (
                    <Box
                      key={cat.id}
                      onClick={() => setSelectedCategoryId(cat.id)}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 2, p: 2, mb: 1,
                        border: 1, borderRadius: 2,
                        borderColor: selectedCategoryId === cat.id ? 'primary.main' : 'grey.300',
                        backgroundColor: selectedCategoryId === cat.id ? 'primary.lightest' : 'white',
                        cursor: 'pointer', '&:hover': { borderColor: 'primary.light', backgroundColor: 'grey.50' }
                      }}
                    >
                      <img src={cat.imageUrl || 'https://via.placeholder.com/60'} alt={cat.name} style={{ width: 60, height: 60, borderRadius: '8px', objectFit: 'cover' }} />
                      <Box flexGrow={1}>
                        <Typography variant="subtitle1" fontWeight="bold">{cat.name}</Typography>
                        <Typography variant="body2" color="text.secondary">ID: {cat.id}</Typography>
                        <Typography variant="body2" color="text.secondary">Title Name: {cat.titleName}</Typography>
                        <Typography variant="caption" color="primary">{getSubCategoryCount(cat.id)} Aarti(s)</Typography>
                      </Box>
                      <Box display="flex" flexDirection="column" gap={1}>
                        <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); openEditCategory(cat); }}>
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); openDeleteCategory(cat); }}>
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Typography sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>No Aarti categories found.</Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Right Column - Subcategories */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" gutterBottom fontWeight="bold">Aarti Content</Typography>
              <Box sx={{ flex: 1, overflow: 'auto' }}>
                {filteredSubCategories.length > 0 ? (
                  filteredSubCategories.map(sub => (
                    <Box key={sub.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, mb: 1, border: 1, borderRadius: 2, borderColor: 'grey.200' }}>
                      <img src={sub.imageUrl || 'https://via.placeholder.com/80x60'} alt={sub.name} style={{ width: 80, height: 60, borderRadius: '6px', objectFit: 'cover' }} />
                      <Box flexGrow={1}>
                        <Typography variant="subtitle1" fontWeight="bold" color="secondary.dark">{sub.name}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">ID: {sub.id}</Typography>
                        <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                          {sub.description.split(' ').slice(0, 14).join(' ')}{sub.description.split(' ').length > 14 ? '...' : ''}
                        </Typography>
                      </Box>
                      <Box display="flex" flexDirection="column" gap={1}>
                        <IconButton size="small" color="primary" onClick={() => openEditSubCategory(sub)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => openDeleteSubCategory(sub)}>
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Typography sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>
                    {selectedCategoryId ? 'No Aarti content for this category.' : 'Please select a category.'}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* --- Dialogs --- */}

      {/* Add Category Dialog */}
      <Dialog open={openCategoryDialog} onClose={() => setOpenCategoryDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add Aarti Category</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Title Name (e.g., Ganesh Aarti)" margin="normal" value={newCategory.titleName} onChange={e => setNewCategory({ ...newCategory, titleName: e.target.value })} />
          <TextField fullWidth label="God Name (e.g., Ganesh Ji)" margin="normal" value={newCategory.name} onChange={e => setNewCategory({ ...newCategory, name: e.target.value })} />
          <TextField fullWidth label="Image URL" margin="normal" value={newCategory.imageUrl} onChange={e => setNewCategory({ ...newCategory, imageUrl: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCategoryDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateCategory} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Category Dialog */}      
      <Dialog open={openEditCategoryDialog} onClose={() => setOpenEditCategoryDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Aarti Category</DialogTitle>
        {editingCategory && (
          <>
            <DialogContent>
              {/* ✅ Added missing titleName field */}
              <TextField
                fullWidth
                label="Title Name"
                margin="normal"
                value={editingCategory.titleName || ''}
                onChange={e => setEditingCategory({ ...editingCategory, titleName: e.target.value })}
              />
              <TextField
                fullWidth
                label="God Name"
                margin="normal"
                value={editingCategory.name || ''}
                onChange={e => setEditingCategory({ ...editingCategory, name: e.target.value })}
              />

              {/* ✅ Replace Image URL text field with file upload */}
              <Box sx={{ mt: 2, p: 2, border: 1, borderRadius: 2, borderColor: 'grey.300' }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Category Image
                </Typography>

                {/* Show existing image */}
                {editingCategory.imageUrl && !editingCategory.imageFile && (
                  <Box sx={{ mb: 1 }}>
                    <img
                      src={editingCategory.imageUrl}
                      alt="Current"
                      style={{ width: 60, height: 60, borderRadius: 8, objectFit: 'cover' }}
                    />
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      Current image
                    </Typography>
                  </Box>
                )}

                {/* Show new image preview */}
                {editingCategory.imageFile && (
                  <Box sx={{ mb: 1 }}>
                    <img
                      src={URL.createObjectURL(editingCategory.imageFile)}
                      alt="New preview"
                      style={{ width: 60, height: 60, borderRadius: 8, objectFit: 'cover' }}
                    />
                    <Typography variant="caption" color="success.main" display="block" sx={{ mt: 0.5 }}>
                      ✓ {editingCategory.imageFile.name}
                    </Typography>
                  </Box>
                )}

                <Button variant="outlined" component="label" size="small">
                  {editingCategory.imageFile ? 'Change Image' : 'Upload New Image'}
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={e => {
                      const file = e.target.files[0];
                      if (file) setEditingCategory({ ...editingCategory, imageFile: file });
                    }}
                  />
                </Button>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenEditCategoryDialog(false)}>Cancel</Button>
              <Button onClick={handleUpdateCategory} variant="contained">Save Changes</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Delete Category Dialog */}
      <Dialog open={openDeleteCategoryDialog} onClose={() => setOpenDeleteCategoryDialog(false)} fullWidth maxWidth="xs">
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete the category: <strong>{deletingCategory?.name}</strong>?</Typography>
          <Typography color="error" variant="body2">This will also delete all associated Aarti content and cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteCategoryDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteCategory} variant="contained" color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Add Sub-Category Dialog */}
      <Dialog open={openSubCategoryDialog} onClose={() => setOpenSubCategoryDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add Aarti Content</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel id="aarti-category-select-label">Category</InputLabel>
            <Select
              labelId="aarti-category-select-label"
              value={newSubCategory.aartiCategoryId}
              label="Category"
              onChange={e => setNewSubCategory({ ...newSubCategory, aartiCategoryId: e.target.value })}
            >
              {categories.map(cat => (
                <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField fullWidth label="Aarti Name (e.g., Jai Ganesh Deva)" margin="normal" value={newSubCategory.name} onChange={e => setNewSubCategory({ ...newSubCategory, name: e.target.value })} />
          <TextField fullWidth label="Aarti Content / Description" multiline rows={4} margin="normal" value={newSubCategory.description} onChange={e => setNewSubCategory({ ...newSubCategory, description: e.target.value })} />
          <TextField fullWidth label="Image URL" margin="normal" value={newSubCategory.imageUrl} onChange={e => setNewSubCategory({ ...newSubCategory, imageUrl: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSubCategoryDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateSubCategory} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Sub-Category Dialog */}
      <Dialog open={openEditSubCategoryDialog} onClose={() => setOpenEditSubCategoryDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Aarti Content</DialogTitle>
        {editingSubCategory && (
          <>
            <DialogContent>
              <FormControl fullWidth margin="normal">
                <InputLabel id="edit-aarti-category-select-label">Category</InputLabel>
                <Select
                  labelId="edit-aarti-category-select-label"
                  value={editingSubCategory.aartiCategoryId}
                  label="Category"
                  onChange={e => setEditingSubCategory({ ...editingSubCategory, aartiCategoryId: e.target.value })}
                >
                  {categories.map(cat => (
                    <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Aarti Name"
                margin="normal"
                value={editingSubCategory.name}
                onChange={e => setEditingSubCategory({ ...editingSubCategory, name: e.target.value })}
              />

              <TextField
                fullWidth
                label="Aarti Content / Description"
                multiline
                rows={4}
                margin="normal"
                value={editingSubCategory.description}
                onChange={e => setEditingSubCategory({ ...editingSubCategory, description: e.target.value })}
              />

              <TextField
                fullWidth
                label="Aarti Video URLs"
                margin="normal"
                value={editingSubCategory.videoUrls || ''}
                onChange={e => setEditingSubCategory({ ...editingSubCategory, videoUrls: e.target.value })}
              />

              {/* Image Upload */}
              <Box sx={{ mt: 2, p: 2, border: 1, borderRadius: 2, borderColor: 'grey.300' }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Aarti Image
                </Typography>

                {/* Show existing image preview */}
                {editingSubCategory.imageUrl && !editingSubCategory.imageFile && (
                  <Box sx={{ mb: 1 }}>
                    <img
                      src={editingSubCategory.imageUrl}
                      alt="Current"
                      style={{ width: 80, height: 60, borderRadius: 6, objectFit: 'cover' }}
                    />
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      Current image
                    </Typography>
                  </Box>
                )}

                {/* Show new image preview if selected */}
                {editingSubCategory.imageFile && (
                  <Box sx={{ mb: 1 }}>
                    <img
                      src={URL.createObjectURL(editingSubCategory.imageFile)}
                      alt="New preview"
                      style={{ width: 80, height: 60, borderRadius: 6, objectFit: 'cover' }}
                    />
                    <Typography variant="caption" color="success.main" display="block" sx={{ mt: 0.5 }}>
                      ✓ New image: {editingSubCategory.imageFile.name}
                    </Typography>
                  </Box>
                )}

                <Button variant="outlined" component="label" size="small">
                  {editingSubCategory.imageFile ? 'Change Image' : 'Upload New Image'}
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={e => {
                      const file = e.target.files[0];
                      if (file) setEditingSubCategory({ ...editingSubCategory, imageFile: file });
                    }}
                  />
                </Button>
              </Box>

            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenEditSubCategoryDialog(false)}>Cancel</Button>
              <Button onClick={handleUpdateSubCategory} variant="contained">Save Changes</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Delete Sub-Category Dialog */}
      <Dialog open={openDeleteSubCategoryDialog} onClose={() => setOpenDeleteSubCategoryDialog(false)} fullWidth maxWidth="xs">
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete the Aarti: <strong>{deletingSubCategory?.name}</strong>?</Typography>
          <Typography color="error" variant="body2">This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteSubCategoryDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteSubCategory} variant="contained" color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* YouTube CSV Upload Dialog */}
      <Dialog
        open={openYouTubeDialog}
        onClose={() => { setOpenYouTubeDialog(false); setYoutubeFile(null); }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Upload YouTube CSV</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload a CSV file to update Aarti content with YouTube video URLs.
          </Typography>

          <Box sx={{ p: 2, border: 1, borderRadius: 2, borderColor: 'grey.300' }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              YouTube CSV File
            </Typography>
            <Button variant="outlined" component="label" size="small">
              Choose File
              <input
                type="file"
                accept=".csv"
                hidden
                onChange={e => setYoutubeFile(e.target.files[0])}
              />
            </Button>
            {youtubeFile && (
              <Typography variant="caption" color="success.main" display="block" sx={{ mt: 1 }}>
                ✓ {youtubeFile.name}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenYouTubeDialog(false); setYoutubeFile(null); }}>
            Cancel
          </Button>
          <Button
            onClick={handleYouTubeCsvUpload}
            variant="contained"
            color="error"
            disabled={uploadingYoutube || !youtubeFile}
          >
            {uploadingYoutube ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload CSV Dialog */}
      <Dialog
        open={openUploadDialog}
        onClose={() => { setOpenUploadDialog(false); setUploadFiles({ details: null, content: null }); }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Upload Aarti CSV</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload both CSV files to bulk import Aarti data.
          </Typography>

          {/* Details File */}
          <Box sx={{ mb: 2, p: 2, border: 1, borderRadius: 2, borderColor: 'grey.300' }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Details File
            </Typography>
            <Button variant="outlined" component="label" size="small">
              Choose File
              <input
                type="file"
                accept=".csv"
                hidden
                onChange={e => setUploadFiles(prev => ({ ...prev, details: e.target.files[0] }))}
              />
            </Button>
            {uploadFiles.details && (
              <Typography variant="caption" color="success.main" display="block" sx={{ mt: 1 }}>
                ✓ {uploadFiles.details.name}
              </Typography>
            )}
          </Box>

          {/* Content File */}
          <Box sx={{ p: 2, border: 1, borderRadius: 2, borderColor: 'grey.300' }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Content File
            </Typography>
            <Button variant="outlined" component="label" size="small">
              Choose File
              <input
                type="file"
                accept=".csv"
                hidden
                onChange={e => setUploadFiles(prev => ({ ...prev, content: e.target.files[0] }))}
              />
            </Button>
            {uploadFiles.content && (
              <Typography variant="caption" color="success.main" display="block" sx={{ mt: 1 }}>
                ✓ {uploadFiles.content.name}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenUploadDialog(false); setUploadFiles({ details: null, content: null }); }}>
            Cancel
          </Button>
          <Button
            onClick={handleUploadCsv}
            variant="contained"
            color="success"
            disabled={uploading || !uploadFiles.details || !uploadFiles.content}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast Container */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </Box>
  );
}